import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, Container, FormControlLabel, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import * as lodash from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { IResponse } from '../../api/httpClient';
import * as ScenarioHelper from '../../api/scenario/scenarioHelper';
import {
    draft,
    propellerEquippedWithNozzles,
    scenarioPhysicalData,
    vesselBeamBs,
    vesselType
} from '../../conversion/scenario';

import {
    distanceBetweenPropellerAxes,
    enginePhysicalData,
    maximumPowerOfThePropeller,
    numberOfPropellers,
    percentageOfUseInPortManeuvers,
    propellerDiameter,
    propellerDirectionWithRespectToTheQuay,
    typeOfPropeller,
} from '../../conversion/engine';
import { route } from '../../route/Index';
import DropDown from '../../shared/component/DropDown';
import InputText from '../../shared/component/InputText';
import { ENumberOfPropellers, EPropellerDirectionWithRespectToTheQuay, EPropellerEquippedWithNozzles, EStatus, ETypeOfPropeller, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IScenario, TInputScenario } from '../../shared/iProject';
import variable from '../../shared/style/variable';

interface IScenarioDetailProps {}

const ScenarioContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const GeometryDetail = (props: IScenarioDetailProps) => {
    let { param } = useParams();

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = React.useState(false);
    const [scenario, setScenario] = React.useState<IScenario | null>();

    React.useEffect(() => {
        const fetchScenario = async () => {
            setIsLoading(true);
            let vesselType = '';
            if (param === 'newScenario') {
                let res = await ScenarioHelper.getScenarioPreviewAsync(1, 1);
                vesselType = res.data.value[0].vesselType;
            } else {
                vesselType = param!;
            }
            let initialScenario = await ScenarioHelper.getScenarioByVesselTypeAsync(vesselType);
            setScenario(initialScenario.data);
            setIsLoading(false);
        };
        fetchScenario();
    }, []);

    const handleClickSave = async () => {
        let res: IResponse<IScenario>;
        if (param === 'newScenario') {
            res = await ScenarioHelper.insertScenarioAsync(scenario!);
        } else {
            res = await ScenarioHelper.updateScenarioAsync(scenario!);
        }
        if (res.status == 200) {
            Swal.fire({
                title: t('savedScenario'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    const handleChangeScenario = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        setScenario({
            ...scenario!,
            [name]: value,
        });
    };

    const handleChangeEngine = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value, checked } = event.target;

        if (name == 'status') {
            value = checked ? EStatus.Public : EStatus.Test;
        }

        let newScenario = lodash.cloneDeep(scenario)!;
        newScenario.engines![index] = {
            ...newScenario.engines![index],
            [name]: value,
        };
        setScenario(newScenario);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <ArrowForwardIosIcon style={{ fill: variable.greenLight }} />
                <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                    {param == 'newScenario' ? t('addNewScenario') : t('updateScenario')}
                </Typography>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && scenario && (
                <ScenarioContainer maxWidth='md'>
                    <Stack height={'95%'} direction='column'>
                        <Stack padding={'0 1rem'}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={scenario?.status == EStatus.Public} onChange={e => setScenario({ ...scenario, status: e.target.checked ? EStatus.Public : EStatus.Test })} />
                                }
                                label={t('public')}
                            />
                        </Stack>
                        <Stack height={'100%'} direction={'row'} spacing={1} position={'relative'}>
                            <Stack variant='scrollable'>
                                <InputText isNumber customValue={scenario[vesselType]} id={vesselType} inputLabel={t(vesselType)} name={vesselType} customOnChange={handleChangeScenario} />
                                <DropDown
                                    inputLabel={t(propellerEquippedWithNozzles)}
                                    labelId={`${propellerEquippedWithNozzles}-label`}
                                    id={propellerEquippedWithNozzles}
                                    name={propellerEquippedWithNozzles}
                                    value={scenario[propellerEquippedWithNozzles]}
                                    onChange={handleChangeScenario}>
                                    <MenuItem value={EPropellerEquippedWithNozzles.Yes}>{t('yes')}</MenuItem>
                                    <MenuItem value={EPropellerEquippedWithNozzles.No}>{t('no')}</MenuItem>
                                </DropDown>
                                <InputText
                                    isNumber
                                    key={draft}
                                    customValue={scenario[draft]}
                                    id={draft}
                                    inputLabel={t(draft, {
                                        unitOfMeasure: scenarioPhysicalData[draft as TInputScenario]?.unitOfMeasure[EUnitOfMeasure.Metric],
                                    })}
                                    name={draft}
                                    customOnChange={handleChangeScenario}
                                />
                                <InputText
                                    isNumber
                                    key={vesselBeamBs}
                                    customValue={scenario[vesselBeamBs]}
                                    id={vesselBeamBs}
                                    inputLabel={t(vesselBeamBs, {
                                        unitOfMeasure: scenarioPhysicalData[vesselBeamBs as TInputScenario]?.unitOfMeasure[EUnitOfMeasure.Metric],
                                    })}
                                    name={vesselBeamBs}
                                    customOnChange={handleChangeScenario}
                                />
                                {scenario.engines?.map((x, i) => (
                                    <Accordion disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={x.id!.toString()} id={x.id!.toString()}>
                                            <Typography>{x.translations![0].name}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <FormControlLabel name='status' control={<Checkbox checked={x!.status == EStatus.Public} onChange={e => handleChangeEngine(e, i)} />} label={t('public')} />
                                            <DropDown
                                                disabled
                                                inputLabel={t(typeOfPropeller)}
                                                labelId={`${typeOfPropeller}-label`}
                                                id={typeOfPropeller}
                                                name={typeOfPropeller}
                                                value={x.typeOfPropeller}
                                                onChange={e => handleChangeEngine(e, i)}>
                                                <MenuItem value={ETypeOfPropeller.MainPropeller}>{t('mainPropeller')}</MenuItem>
                                                <MenuItem value={ETypeOfPropeller.BowThruster}>{t('bowThruster')}</MenuItem>
                                            </DropDown>
                                            <DropDown
                                                disabled
                                                inputLabel={t(propellerDirectionWithRespectToTheQuay)}
                                                labelId={`${propellerDirectionWithRespectToTheQuay}-label`}
                                                id={propellerDirectionWithRespectToTheQuay}
                                                name={propellerDirectionWithRespectToTheQuay}
                                                value={x.propellerDirectionWithRespectToTheQuay}
                                                onChange={e => handleChangeEngine(e, i)}>
                                                <MenuItem value={EPropellerDirectionWithRespectToTheQuay.Parallel}>{t('parallel')}</MenuItem>
                                                <MenuItem value={EPropellerDirectionWithRespectToTheQuay.Transversal}>{t('transversal')}</MenuItem>
                                            </DropDown>
                                            <DropDown
                                                disabled
                                                inputLabel={t(numberOfPropellers)}
                                                labelId={`${numberOfPropellers}-label`}
                                                id={numberOfPropellers}
                                                name={numberOfPropellers}
                                                value={x.numberOfPropellers}
                                                onChange={e => handleChangeEngine(e, i)}>
                                                <MenuItem value={ENumberOfPropellers.Single}>{ENumberOfPropellers.Single}</MenuItem>
                                                <MenuItem value={ENumberOfPropellers.Double}>{ENumberOfPropellers.Double}</MenuItem>
                                            </DropDown>
                                            <InputText
                                                customValue={x.distanceBetweenPropellerAxes}
                                                id={distanceBetweenPropellerAxes}
                                                hidden={x.numberOfPropellers == ENumberOfPropellers.Single}
                                                isNumber
                                                inputLabel={t(distanceBetweenPropellerAxes, {
                                                    unitOfMeasure: enginePhysicalData[distanceBetweenPropellerAxes].unitOfMeasure[EUnitOfMeasure.Metric],
                                                })}
                                                name={distanceBetweenPropellerAxes}
                                                customOnChange={e => handleChangeEngine(e, i)}
                                            />
                                            <InputText
                                                customValue={x.propellerDiameter}
                                                id={propellerDiameter}
                                                isNumber
                                                inputLabel={t(propellerDiameter, {
                                                    unitOfMeasure: enginePhysicalData[propellerDiameter].unitOfMeasure[EUnitOfMeasure.Metric],
                                                })}
                                                name={propellerDiameter}
                                                customOnChange={e => handleChangeEngine(e, i)}
                                            />
                                            <InputText
                                                customValue={x.maximumPowerOfThePropeller}
                                                id={maximumPowerOfThePropeller}
                                                isNumber
                                                inputLabel={t(maximumPowerOfThePropeller, {
                                                    unitOfMeasure: enginePhysicalData[maximumPowerOfThePropeller].unitOfMeasure[EUnitOfMeasure.Metric],
                                                })}
                                                name={maximumPowerOfThePropeller}
                                                customOnChange={e => handleChangeEngine(e, i)}
                                            />
                                            <InputText
                                                customValue={x.percentageOfUseInPortManeuvers}
                                                id={percentageOfUseInPortManeuvers}
                                                isNumber
                                                inputLabel={t(percentageOfUseInPortManeuvers, {
                                                    unitOfMeasure: enginePhysicalData[percentageOfUseInPortManeuvers].unitOfMeasure[EUnitOfMeasure.Metric],
                                                })}
                                                name={percentageOfUseInPortManeuvers}
                                                customOnChange={e => handleChangeEngine(e, i)}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        </Stack>
                        <Stack alignItems='end' margin={'2rem 0 0 0'}>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <Button variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </ScenarioContainer>
            )}
        </>
    );
};

export default isAdmin(GeometryDetail);
